import {useCallback, useEffect, useRef, useState} from 'react';
import {Box, CircularProgress, Drawer, Paper, useMediaQuery, useTheme} from '@mui/material';
import {ActiveSelection, FabricObject} from 'fabric';
import {
    DndContext,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {snapCenterToCursor} from "@dnd-kit/modifiers";
import {FloorPlanItem} from './types';
import BottomMenu from './shared/BottomMenu';
import EditSidebar from './edit/EditSidebar';
import AlignmentToolbar from './edit/AlignmentToolbar';
import EditTableGroupsOverlay from './edit/EditTableGroupsOverlay';
import {useCanvasDrop} from './hooks/useCanvasDrop';
import {useElementDuplication} from './hooks/useElementDuplication';
import {useUndoRedo} from './hooks/useUndoRedo';
import PreviewDropTableElementDesktop, {AddPanelDragData} from './edit/PreviewDropTableElementDesktop';
import PreviewDropTableElementMobile from "./edit/PreviewDropTableElementMobile";
import {
    createShapeElement,
    createTableGroup,
    createTextElement,
    createWallElement,
    rebuildElement,
} from "./utils/fabric-factory";
import {useFloorplanContext} from "./hooks/useFloorplanContext";
import {
    buildNormalizedItemFromObject,
    clampObjectScalingToMinimum,
    objectHasResidualScale,
} from "./utils/resize-utils";
import {snapObjectAnchorPoint} from "./utils/snap-geometry";
import {
    createTableGroup as createStoredTableGroup,
    getTableGroupMemberId,
    getTableGroupResourceId,
    getStoredTableGroups,
    StoredTableGroup,
    TableGroupTableRef,
    TableGroupKind,
    tableIdSetMatches,
    upsertTableGroup,
} from "./utils/table-groups";
import {generateId} from "../../../../utils/generate-id";
import {useTranslation} from "react-i18next";
import {useMobileResizableDrawer} from "./hooks/useMobileResizableDrawer";

interface Props {
    floorPlanItems: FloorPlanItem[];
    isSaving?: boolean;
    onDirtyChange?: (dirty: boolean) => void;
}

// Inner component that uses useCanvasDrop - must be inside DndContext
const CanvasDropHandler: React.FC<{
    onElementDropped: (element: FabricObject, id: string) => void;
}> = ({onElementDropped}) => {
    const {canvas, containerRef, showSeatsAroundTable} = useFloorplanContext();

    useCanvasDrop({
        canvas,
        containerRef,
        showSeatsAroundTable,
        onElementDropped,
    });

    return null;
};

const EditCanvasView: React.FC<Props> = ({floorPlanItems, isSaving, onDirtyChange}) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery('(max-width:900px)');
    const {
        canvas,
        isInitialized,
        containerRef,
        canvasElRef,
        showSeatsAroundTable,
        updateAllTableElementsLocally,
        restaurantDraft,
        setRestaurantDraft,
    } = useFloorplanContext();
    const isRebuilding = useRef(false);
    const suppressNextSelectionEditOpen = useRef(false);
    const previousShowSeatsAroundTable = useRef(showSeatsAroundTable);

    // UI state
    const [selectedElements, setSelectedElements] = useState<FabricObject[]>([]);
    const [editingElementId, setEditingElementId] = useState<string | null>(null);
    const [elementsLoaded, setElementsLoaded] = useState(false);
    const [, forceUpdate] = useState(0);
    const [groupMode, setGroupMode] = useState(false);
    const [selectedGroupTableIds, setSelectedGroupTableIds] = useState<string[]>([]);

    // DnD state
    const [activeDrag, setActiveDrag] = useState<AddPanelDragData | null>(null);
    const {
        drawerOpen,
        drawerHeight,
        isResizingDrawer,
        minHeight: mobileDrawerMinHeight,
        maxHeightVh: mobileDrawerMaxHeightVh,
        closeDrawer,
        handleOpenDrawer,
        handleDrawerResizeStart,
        resetDrawerHeight,
    } = useMobileResizableDrawer();

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 8}}),
        useSensor(TouchSensor, {activationConstraint: {delay: 150, tolerance: 8}})
    );

    const handleDragStart = useCallback((event: DragStartEvent) => {
        const data = event.active.data.current as AddPanelDragData | undefined;
        if (data) {
            setActiveDrag(data);
            if (isMobile) closeDrawer();
        }
    }, [closeDrawer, isMobile]);

    const handleDragEnd = useCallback(() => {
        setActiveDrag(null);
    }, []);

    const handleDragCancel = useCallback(() => {
        setActiveDrag(null);
    }, []);

    const refreshElements = useCallback(() => {
        forceUpdate(n => n + 1);
    }, []);

    const tableGroups = getStoredTableGroups(restaurantDraft?.meta as Record<string, any> | undefined);
    // Re-selecting the exact same tables should update the existing stored group instead of creating a duplicate.
    const matchingSelectionGroup = tableGroups.find((group) =>
        tableIdSetMatches(group.tableIds, selectedGroupTableIds)
    ) ?? null;

    const syncSelectionState = useCallback((preserveEditingId = false) => {
        if (!canvas) return;

        const activeObj = canvas.getActiveObject();
        let selected: FabricObject[] = [];

        if (activeObj instanceof ActiveSelection) {
            activeObj.lockRotation = true;
            selected = activeObj.getObjects();
        } else if (activeObj?.floorPlanItem) {
            selected = [activeObj as FabricObject];
        }

        const validElements = selected.filter(obj => !!obj.floorPlanItem);
        setSelectedElements(validElements);
        setEditingElementId((current) => {
            if (validElements.length === 1) {
                return validElements[0].floorPlanItem?.id ?? null;
            }
            if (preserveEditingId && current) {
                return canvas.getObjects().some(obj => obj.floorPlanItem?.id === current) ? current : null;
            }
            return null;
        });
    }, [canvas]);

    // Undo/redo
    const {
        recordSnapshot,
        initSnapshot,
        undo,
        redo,
        canUndo,
        canRedo,
        isRestoring,
    } = useUndoRedo(canvas, showSeatsAroundTable, () => {
        refreshElements();
        syncSelectionState(true);
    }, onDirtyChange);

    const recordAndRefresh = useCallback(() => {
        recordSnapshot();
        forceUpdate(n => n + 1);
    }, [recordSnapshot]);

    const getElements = useCallback((): FabricObject[] => {
        if (!canvas) return [];
        return canvas.getObjects().filter(
            (obj): obj is FabricObject => !!obj.floorPlanItem
        );
    }, [canvas]);

    const getCurrentTableGroupRefs = useCallback((): TableGroupTableRef[] => {
        return getElements()
            .map((element) => {
                const tableId = getTableGroupMemberId(element.floorPlanItem);
                const resourceId = getTableGroupResourceId(element.floorPlanItem);
                if (!tableId || !resourceId) return null;
                return {tableId, resourceId};
            })
            .filter((table): table is TableGroupTableRef => table !== null);
    }, [getElements]);

    const updateTableGroups = useCallback((updater: (groups: ReturnType<typeof getStoredTableGroups>) => ReturnType<typeof getStoredTableGroups>) => {
        setRestaurantDraft((prev) => {
            if (!prev) return prev;
            const currentGroups = getStoredTableGroups(prev.meta as Record<string, any> | undefined);
            return {
                ...prev,
                meta: {
                    ...prev.meta,
                    tableGroups: updater(currentGroups),
                },
            };
        });
    }, [setRestaurantDraft]);

    const toggleGroupTableSelection = useCallback((tableId: string) => {
        setSelectedGroupTableIds((prev) =>
            prev.includes(tableId)
                ? prev.filter((id) => id !== tableId)
                : [...prev, tableId]
        );
    }, []);

    const handleToggleGroupMode = useCallback(() => {
        setSelectedGroupTableIds([]);
        setGroupMode((prev) => !prev);
    }, []);

    const handleCreateTableGroup = useCallback((
        kind: TableGroupKind,
        options?: Pick<StoredTableGroup, 'min' | 'max'>,
    ) => {
        if (selectedGroupTableIds.length < 2) return;
        // Preserve the existing group id when the current selection already maps to one stored group.
        const nextGroupId = matchingSelectionGroup?.id ?? generateId();
        const resourceIdByTableId = new Map(
            getCurrentTableGroupRefs().map((table) => [table.tableId, table.resourceId]),
        );
        const selectedTables = selectedGroupTableIds.map((tableId) => ({
            tableId,
            resourceId: resourceIdByTableId.get(tableId) ?? tableId,
        }));
        updateTableGroups((groups) => createStoredTableGroup(groups, kind, selectedTables, nextGroupId, options));
        setSelectedGroupTableIds([]);
    }, [getCurrentTableGroupRefs, matchingSelectionGroup?.id, selectedGroupTableIds, updateTableGroups]);

    const handleDeleteTableGroup = useCallback((groupId: string) => {
        updateTableGroups((groups) => groups.filter((group) => group.id !== groupId));
        setSelectedGroupTableIds((prev) => {
            const deletedGroup = tableGroups.find((group) => group.id === groupId);
            if (!deletedGroup) return prev;
            const deletedSet = new Set(deletedGroup.tableIds);
            return prev.filter((tableId) => !deletedSet.has(tableId));
        });
    }, [tableGroups, updateTableGroups]);

    const handleUpdateTableGroup = useCallback((
        groupId: string,
        patch: Partial<Pick<StoredTableGroup, 'kind' | 'min' | 'max'>>,
    ) => {
        updateTableGroups((groups) => {
            const currentGroup = groups.find((group) => group.id === groupId);
            if (!currentGroup) return groups;
            return upsertTableGroup(groups, {
                ...currentGroup,
                ...patch,
            });
        });
    }, [updateTableGroups]);

    useEffect(() => {
        if (!groupMode) {
            setSelectedGroupTableIds([]);
        }
    }, [groupMode]);

    useEffect(() => {
        if (!canvas) return;

        if (groupMode) {
            canvas.discardActiveObject();
            setSelectedElements([]);
            setEditingElementId(null);
        }

        canvas.selection = !groupMode;
        canvas.getObjects().forEach((obj) => {
            const isEditable = !!obj.floorPlanItem;
            obj.set({
                selectable: isEditable && !groupMode,
                evented: isEditable && !groupMode,
                hasControls: isEditable && !groupMode,
                hasBorders: isEditable && !groupMode,
                lockMovementX: groupMode || !isEditable,
                lockMovementY: groupMode || !isEditable,
            });
        });
        canvas.requestRenderAll();
    }, [canvas, groupMode]);

    // Canvas selection events
    useEffect(() => {
        if (!canvas) return;

        const handleSelection = (e: { selected?: FabricObject[] }) => {
            const activeObj = canvas.getActiveObject();

            let selected = e.selected ?? [];
            if (activeObj instanceof ActiveSelection) {
                activeObj.lockRotation = true;
                selected = activeObj.getObjects();
            }

            const validElements = selected.filter(obj => !!obj.floorPlanItem);
            setSelectedElements(validElements);

            if (suppressNextSelectionEditOpen.current) {
                suppressNextSelectionEditOpen.current = false;
                setEditingElementId(null);
            } else if (validElements.length === 1) {
                setEditingElementId(validElements[0].floorPlanItem?.id ?? null);
            } else {
                setEditingElementId(null);
            }
        };

        const handleSelectionCleared = () => {
            setSelectedElements([]);
            setEditingElementId(null);
        };

        canvas.on('selection:created', handleSelection);
        canvas.on('selection:updated', handleSelection);
        canvas.on('selection:cleared', handleSelectionCleared);

        return () => {
            canvas.off('selection:created', handleSelection);
            canvas.off('selection:updated', handleSelection);
            canvas.off('selection:cleared', handleSelectionCleared);
        };
    }, [canvas]);

    // Normalize resize interactions in one place for all editable element kinds.
    useEffect(() => {
        if (!canvas) return;

        const handleObjectScaling = (e: any) => {
            const obj = e.target as FabricObject | undefined;
            if (!obj || isRebuilding.current) return;

            // Keep the object valid while dragging.
            if (clampObjectScalingToMinimum(obj, e.transform?.originX, e.transform?.originY)) {
                canvas.requestRenderAll();
            }
        };

        const handleObjectModified = (e: any) => {
            if (isRebuilding.current) return;

            const obj = e.target as FabricObject | undefined;
            if (!obj) return;
            const nativeEvent = e.e as MouseEvent | undefined;
            const action = e.transform?.action as string | undefined;
            const hadResidualScale = objectHasResidualScale(obj);
            const isResizeAction = hadResidualScale || action === 'scale' || action === 'scaleX' || action === 'scaleY' || action === 'scaleEqually' || action === 'resizing' || action === 'modifyPolyline';
            const skipSnap = action === 'drag'
                || isResizeAction
                || (!!nativeEvent && (nativeEvent.shiftKey || nativeEvent.ctrlKey || nativeEvent.metaKey));

            let normalizedTarget = obj;

            if (hadResidualScale) {
                // Bake live Fabric scale back into the stored item model.
                const newItem = buildNormalizedItemFromObject(obj);
                if (newItem) {
                    isRebuilding.current = true;
                    normalizedTarget = rebuildElement(obj, canvas, newItem, showSeatsAroundTable);
                    isRebuilding.current = false;
                }
            }

            if (!skipSnap) {
                snapObjectAnchorPoint(normalizedTarget);
            }
            syncSelectionState();

            refreshElements();

            // Record snapshot for undo (covers move, rotate, scale, wall endpoint drags).
            if (!isRestoring.current) recordSnapshot();
        };

        canvas.on('object:scaling', handleObjectScaling);
        canvas.on('object:modified', handleObjectModified);
        return () => {
            canvas.off('object:scaling', handleObjectScaling);
            canvas.off('object:modified', handleObjectModified);
        };
    }, [canvas, showSeatsAroundTable, recordSnapshot, isRestoring, refreshElements, syncSelectionState]);

    // Load floor plan items
    useEffect(() => {
        if (!canvas || !isInitialized) return;

        setElementsLoaded(false);
        setSelectedElements([]);
        setEditingElementId(null);
        canvas.discardActiveObject();
        canvas.getObjects().forEach(obj => canvas.remove(obj));

        if (floorPlanItems.length === 0) {
            canvas.renderAll();
            setElementsLoaded(true);
            refreshElements();
            initSnapshot();
            return;
        }

        floorPlanItems.forEach(item => {
            let element: FabricObject | null = null;

            if (item.table) {
                element = createTableGroup(item, showSeatsAroundTable);
            } else if (item.element) {
                const el = item.element;
                if (el.kind === 'text') {
                    element = createTextElement(item);
                } else if (el.kind === 'shape' && el.shape) {
                    element = createShapeElement(item);
                } else if (el.kind === 'wall') {
                    element = createWallElement(item);
                }
            }

            if (element) canvas.add(element);
        });

        canvas.renderAll();
        setElementsLoaded(true);
        refreshElements();
        initSnapshot();
    }, [canvas, floorPlanItems, isInitialized, refreshElements, initSnapshot]);

    // Toggle seat display in-place without rebuilding the whole canvas from prop data.
    useEffect(() => {
        if (!canvas || !isInitialized) return;

        const hasSeatPreferenceChanged = previousShowSeatsAroundTable.current !== showSeatsAroundTable;
        previousShowSeatsAroundTable.current = showSeatsAroundTable;

        if (!hasSeatPreferenceChanged || canvas.getObjects().length === 0) return;

        const updatedTables = updateAllTableElementsLocally((item) => item);
        if (updatedTables.length > 0) {
            canvas.requestRenderAll();
            refreshElements();
            syncSelectionState(true);
        }
    }, [canvas, isInitialized, showSeatsAroundTable, updateAllTableElementsLocally, refreshElements, syncSelectionState]);

    // Drop callback
    const handleElementDropped = useCallback((element: FabricObject, id: string) => {
        if (groupMode) return;
        setSelectedElements([element]);
        setEditingElementId(id);
        recordSnapshot();
        refreshElements();
    }, [groupMode, refreshElements, recordSnapshot]);

    // Duplication callback
    const handleDuplicated = useCallback((elements: FabricObject[], isSingle: boolean) => {
        if (groupMode) return;
        setSelectedElements(elements);
        if (isSingle) {
            setEditingElementId(elements[0].floorPlanItem?.id ?? null);
        } else {
            setEditingElementId(null);
        }
        recordSnapshot();
        refreshElements();
    }, [groupMode, refreshElements, recordSnapshot]);

    // Delete callback
    const handleDeleted = useCallback(() => {
        if (groupMode) return;
        setSelectedElements([]);
        setEditingElementId(null);
        recordSnapshot();
        refreshElements();
    }, [groupMode, refreshElements, recordSnapshot]);

    const {duplicateElement} = useElementDuplication({
        onDuplicated: handleDuplicated,
        onDeleted: handleDeleted,
    });

    const handleActivateElement = useCallback((element: FabricObject) => {
        if (groupMode) return;
        if (!canvas) return;
        suppressNextSelectionEditOpen.current = true;
        canvas.setActiveObject(element);
        canvas.requestRenderAll();
        setSelectedElements([element]);
        setEditingElementId(null);
    }, [canvas, groupMode]);

    // Multi-select from sidebar
    const handleMultiSelect = useCallback((element: FabricObject, shiftKey: boolean) => {
        if (groupMode) return;
        if (!canvas) return;
        suppressNextSelectionEditOpen.current = false;

        if (shiftKey && selectedElements.length > 0) {
            const newSelection = [...selectedElements];
            const index = newSelection.indexOf(element);
            if (index === -1) {
                newSelection.push(element);
            } else {
                newSelection.splice(index, 1);
            }

            if (newSelection.length > 1) {
                canvas.discardActiveObject();
                const activeSelection = new ActiveSelection(newSelection, {canvas});
                activeSelection.lockRotation = true;
                canvas.setActiveObject(activeSelection);
            } else if (newSelection.length === 1) {
                canvas.setActiveObject(newSelection[0]);
            } else {
                canvas.discardActiveObject();
            }
            canvas.requestRenderAll();
            setSelectedElements(newSelection);
        } else {
            canvas.setActiveObject(element);
            canvas.requestRenderAll();
            setSelectedElements([element]);
            setEditingElementId(element.floorPlanItem?.id ?? null);
        }
    }, [canvas, groupMode, selectedElements]);

    // Undo/redo keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const t = e.target;
            if (t instanceof HTMLElement) {
                const tag = t.tagName;
                if (tag === 'INPUT' || tag === 'TEXTAREA' || t.isContentEditable) return;
            }

            if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z')) {
                e.preventDefault();
                if (e.shiftKey) {
                    redo();
                } else {
                    undo();
                }
                return;
            }

            if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || e.key === 'Y')) {
                e.preventDefault();
                redo();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [undo, redo]);

    // Edit element from sidebar
    const handleEditElement = useCallback((elementId: string | null) => {
        if (groupMode) return;
        if (!canvas) return;
        setEditingElementId(elementId);
        if (elementId === null) {
            canvas.discardActiveObject();
            canvas.renderAll();
        }
    }, [canvas, groupMode]);

    return (
        <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
        >
            <CanvasDropHandler onElementDropped={handleElementDropped}/>

            <Box sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                pointerEvents: isSaving ? 'none' : 'auto',
                opacity: isSaving ? 0.6 : 1,
                transition: 'opacity 0.2s ease',
            }}>
                <Box sx={{
                    flex: 1,
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    minWidth: 0,
                    overflow: 'hidden',
                    mr: isMobile ? 0 : 2,
                }}>
                    <Paper
                        variant="outlined"
                        ref={containerRef}
                        sx={{
                            position: 'relative',
                            borderRadius: isMobile ? 2 : 4,
                            overflow: 'hidden',
                            bgcolor: 'rgb(250, 250, 250)',
                            width: '100%',
                            height: '100%',
                        }}
                    >
                        <canvas ref={canvasElRef}
                                style={{position: 'relative', zIndex: 1}}/>

                        <EditTableGroupsOverlay
                            groups={tableGroups}
                            selectedTableIds={selectedGroupTableIds}
                            onToggleTableSelection={toggleGroupTableSelection}
                            interactive={groupMode}
                        />

                        {isSaving && (
                            <Box sx={{
                                position: 'absolute',
                                inset: 0,
                                zIndex: 10,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <CircularProgress size={32}/>
                            </Box>
                        )}

                        {isInitialized && canvas && (
                            <>
                                {selectedElements.length >= 2 && !groupMode && (
                                    <AlignmentToolbar
                                        refreshElements={recordAndRefresh}
                                        canvas={canvas}
                                        selectedElements={selectedElements}
                                    />
                                )}

                                <BottomMenu
                                    onOpenDrawer={isMobile ? handleOpenDrawer : undefined}
                                    onUndo={undo}
                                    onRedo={redo}
                                    canUndo={canUndo}
                                    canRedo={canRedo}
                                />
                            </>
                        )}
                    </Paper>

                    {elementsLoaded && isMobile && (
                        <Drawer
                            variant="temporary"
                            anchor="bottom"
                            open={drawerOpen}
                            onClose={closeDrawer}
                            ModalProps={{keepMounted: true}}
                            SlideProps={{
                                onExited: resetDrawerHeight,
                            }}
                            PaperProps={{
                                sx: {
                                    width: "100%",
                                    height: drawerHeight,
                                    minHeight: mobileDrawerMinHeight,
                                    maxHeight: `${mobileDrawerMaxHeightVh * 100}vh`,
                                    bgcolor: '#fff',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderTopLeftRadius: 16,
                                    borderTopRightRadius: 16,
                                    overflow: 'hidden',
                                    transition: isResizingDrawer
                                        ? 'none'
                                        : theme.transitions.create('height', {
                                            duration: theme.transitions.duration.shortest,
                                        }),
                                },
                            }}
                        >
                            <Box
                                onPointerDown={handleDrawerResizeStart}
                                role="separator"
                                aria-label={t("Resize edit panel")}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: 28,
                                    cursor: 'ns-resize',
                                    touchAction: 'none',
                                    flexShrink: 0,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 42,
                                        height: 5,
                                        borderRadius: 999,
                                        bgcolor: 'rgba(0, 0, 0, 0.22)',
                                    }}
                                />
                            </Box>

                            <Box sx={{flex: 1, overflow: 'auto'}}>
                                <EditSidebar
                                    elements={getElements()}
                                    selectedElements={groupMode
                                        ? getElements().filter((element) => selectedGroupTableIds.includes(element.floorPlanItem?.table?.canvas.id ?? element.floorPlanItem?.table?.resource?.id ?? ''))
                                        : selectedElements}
                                    editingElementId={editingElementId}
                                    onSelectElement={handleMultiSelect}
                                    onActivateElement={handleActivateElement}
                                    onEditElement={handleEditElement}
                                    onElementUpdated={recordAndRefresh}
                                    onDuplicateElement={duplicateElement}
                                    groupMode={groupMode}
                                    selectedGroupTableIds={selectedGroupTableIds}
                                    onToggleGroupMode={handleToggleGroupMode}
                                    onCreateTableGroup={handleCreateTableGroup}
                                    onDeleteTableGroup={handleDeleteTableGroup}
                                    onUpdateTableGroup={handleUpdateTableGroup}
                                    tableGroups={tableGroups}
                                />
                            </Box>
                        </Drawer>
                    )}
                </Box>

                {elementsLoaded && !isMobile && (
                    <Paper
                        variant="outlined"
                        sx={{
                            width: 380,
                            minWidth: 380,
                            flexShrink: 0,
                            borderRadius: 4,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: '#fff',
                        }}
                    >
                        <EditSidebar
                            elements={getElements()}
                            selectedElements={groupMode
                                ? getElements().filter((element) => selectedGroupTableIds.includes(element.floorPlanItem?.table?.canvas.id ?? element.floorPlanItem?.table?.resource?.id ?? ''))
                                : selectedElements}
                            editingElementId={editingElementId}
                            onSelectElement={handleMultiSelect}
                            onActivateElement={handleActivateElement}
                            onEditElement={handleEditElement}
                            onElementUpdated={recordAndRefresh}
                            onDuplicateElement={duplicateElement}
                            groupMode={groupMode}
                            selectedGroupTableIds={selectedGroupTableIds}
                            onToggleGroupMode={handleToggleGroupMode}
                            onCreateTableGroup={handleCreateTableGroup}
                            onDeleteTableGroup={handleDeleteTableGroup}
                            onUpdateTableGroup={handleUpdateTableGroup}
                            tableGroups={tableGroups}
                        />
                    </Paper>
                )}

                {!isMobile && !elementsLoaded &&
                    <Box sx={{width: 380, minWidth: 380, flexShrink: 0}}/>}
            </Box>

            <DragOverlay
                zIndex={1300}
                modifiers={isMobile ? [snapCenterToCursor] : undefined}
                dropAnimation={{
                    duration: 200,
                    easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)'
                }}
            >
                {activeDrag && (
                    isMobile ? (
                        <PreviewDropTableElementMobile
                            icon={activeDrag.icon}
                            label={activeDrag.label}
                        />
                    ) : (
                        <PreviewDropTableElementDesktop
                            type={activeDrag.type}
                            icon={activeDrag.icon}
                            label={activeDrag.label}
                            description={activeDrag.description}
                            isPreview
                        />
                    )
                )}
            </DragOverlay>
        </DndContext>
    );
};

export default EditCanvasView;
